/* eslint-disable no-async-promise-executor */
/* eslint-disable @typescript-eslint/no-use-before-define */
import { Box, Grid, Radio, RadioGroup, Stack, Text } from "@chakra-ui/react";
import {
  accountNickNameMaxLength,
  accountNumberMaxLength,
  AddressType,
  bankNameMaxLength,
  fundingNoteMaxLength,
  getAddressSelection,
  routingNumberMaxLength,
} from "@verivest/shared-components";
import { AddressDisclaimer } from "@verivest/shared-components/src/components/common/distribution-preferences/DistributionPreferenceAddressDisclaimer/AddressDisclaimer";
import { HookFormVeriAddress } from "@verivest/shared-components/src/components/form-controls/HookFormVeriAddress";
import { logError } from "@verivest/shared-components/src/error-handling/logError";
import { useStandardApiHandler } from "@verivest/shared-components/src/error-handling/useStandardApiErrorHandler";
import { useVeriForm } from "@verivest/shared-components/src/react-hook-form";
import dasherize from "dasherize";
import React, { useEffect, useState } from "react";
import { Route, useHistory } from "react-router-dom";
import useSWR from "swr";
import { createAddress } from "../../../../api/creators/accounts/createAddress";
import VeriInput from "../../../../components/general/Input";
import BankAccountVisual from "../../../../components/visual/BankAccountVisual";
import WizardContainer from "../../../../components/wizards/Container";
import useApi from "../../../../hooks/useApi";
import useDynamicConfig from "../../../../hooks/useDynamicConfig";
import usePlacementsApi from "../../../../hooks/usePlacementsApi";
import { DASHBOARD_MANAGER } from "../../../../utils/DynamicConfig";
import { getProperty } from "../../../../utils/utils";
import RightPanelInvestment from "./RightPanelInvestment";

const WizardInvestmentDistributionPreferencesReceive = ({
  basePath,
  onBack,
  investment,
  onFinish,
  distributionPreference,
  asDrawer,
  noRedirect,
  onBackToTypeSelection,
  initialData,
  onClose,
  customAnswers,
  showAddressDisclaimer,
  wizardInvestmentDistributionPreferencesReceiveSelectionProps,
  wizardInvestmentDistributionPreferencesReceiveBankProps,
}) => {
  const history = useHistory();
  const onBackReceive = () =>
    history.replace(basePath + "/distribution/receive");
  const placementsApi = usePlacementsApi();

  const [noRedirectSelection, setNoRedirectSelection] = React.useState("");

  const SelectionRender = () => (
    <WizardInvestmentDistributionPreferencesReceiveSelection
      setNoRedirectSelection={setNoRedirectSelection}
      investment={investment}
      onBack={onBackToTypeSelection || onBack}
      backLabel={onBackToTypeSelection && "Change distribution type"}
      basePath={basePath}
      asDrawer={asDrawer}
      noRedirect={noRedirect}
      distributionMethod={distributionPreference?.distributionMethod}
      onClose={onClose}
      customAnswers={customAnswers}
      {...wizardInvestmentDistributionPreferencesReceiveSelectionProps}
    />
  );

  const CheckRender = () => (
    <WizardInvestmentDistributionPreferencesReceiveCheck
      showAddressDisclaimer={showAddressDisclaimer}
      customAnswers={customAnswers}
      asDrawer={asDrawer}
      noRedirect={noRedirect}
      setNoRedirectSelection={setNoRedirectSelection}
      distributionPreference={distributionPreference}
      investment={investment}
      onBack={
        (onBack && onBackReceive) ||
        (noRedirect && (() => setNoRedirectSelection("")))
      }
      basePath={basePath}
      distributionPreferenceValue="Check"
      submitText={asDrawer ? "Save" : "Continue"}
      onClose={onClose}
      onFinish={async (address, type) => {
        if (distributionPreference && distributionPreference.id) {
          const distData = dasherize({
            id: distributionPreference.id,
            distributionMethod: "Check",
            addressType: type,
            distributionAddress: { id: address.id },
            method1Value: null,
            method2Value: null,
          });
          await placementsApi.updateResource(
            "distribution-preference",
            distData,
          );
        } else {
          const distData = dasherize({
            distributionMethod: "Check",
            addressType: type,
            distributionAddress: { id: address.id },
            method1Value: null,
            method2Value: null,
            account: { id: investment.account.id },
            offering: { id: investment.offering.id },
          });
          await placementsApi.createResource(
            "distribution-preference",
            distData,
          );
        }

        if (onFinish) return onFinish();

        return history.push(`${basePath}/progress`);
      }}
    />
  );

  const BankTransferRender = () => (
    <WizardInvestmentDistributionPreferencesReceiveBank
      {...wizardInvestmentDistributionPreferencesReceiveBankProps}
      customAnswers={customAnswers}
      initialData={initialData}
      asDrawer={asDrawer}
      noRedirect={noRedirect}
      setNoRedirectSelection={setNoRedirectSelection}
      distributionPreference={distributionPreference}
      investment={investment}
      onBack={
        (onBack && onBackReceive) ||
        (noRedirect && (() => setNoRedirectSelection("")))
      }
      basePath={basePath}
      distributionPreferenceValue="Bank transfer"
      submitText={asDrawer ? "Save" : "Continue"}
      onClose={onClose}
      onFinish={async (bankAccount) => {
        if (distributionPreference && distributionPreference.id) {
          const distData = dasherize({
            id: distributionPreference.id,
            distributionMethod: "Bank Transfer",
            bankAccount: { id: bankAccount.id },
            method1Value: null,
            method2Value: null,
          });

          await placementsApi.updateResource(
            "distribution-preference",
            distData,
          );
        } else {
          const distData = dasherize({
            distributionMethod: "Bank Transfer",
            bankAccount: { id: bankAccount.id },
            method1Value: null,
            method2Value: null,
            account: { id: investment.account.id },
            offering: { id: investment.offering.id },
          });
          await placementsApi.createResource(
            "distribution-preference",
            distData,
          );
        }

        if (onFinish) return onFinish();

        return history.push(`${basePath}/progress`);
      }}
    />
  );

  if (noRedirect) {
    if (noRedirectSelection === "check") {
      return CheckRender();
    } else if (
      noRedirectSelection === "bank-transfer" ||
      noRedirectSelection === "Bank Transfer"
    ) {
      return BankTransferRender();
    }

    return SelectionRender();
  }

  return (
    <>
      <Route
        path={`${basePath}/distribution/receive`}
        exact
        render={SelectionRender}
      />
      <Route
        path={`${basePath}/distribution/receive/check`}
        exact
        render={CheckRender}
      />
      <Route
        path={`${basePath}/distribution/receive/bank-transfer`}
        exact
        render={BankTransferRender}
      />
    </>
  );
};

const WizardInvestmentDistributionPreferencesReceiveSelection = ({
  basePath,
  onBack,
  investment,
  asDrawer,
  noRedirect = false,
  setNoRedirectSelection,
  onBackToTypeSelection,
  backLabel,
  distributionMethod = -1,
  onClose,
  title = "How would you like your distributions?",
  checkDescription = "We'll mail a check to your address of choice.",
  transferDescription = "We'll transfer distributions directly to your bank account.",
}) => {
  const [selected, setSelected] = useState(distributionMethod);
  const [error, setError] = useState();
  const history = useHistory();

  const onSubmit = async () => {
    try {
      if (!selected || selected === -1) {
        setError("You need to make a choice");
      }

      const newSelection = selected.toLowerCase().replace(" ", "-");

      if (noRedirect && setNoRedirectSelection) {
        setNoRedirectSelection(newSelection);
        return;
      }

      return history.push(`${basePath}/distribution/receive/${newSelection}`);
    } catch (error) {
      //
    }
  };

  const offering = investment.offering;
  return (
    <WizardContainer
      title={title}
      onSubmit={onSubmit}
      rightExtra={<RightPanelInvestment investment={investment} />}
      message={error}
      onBack={onBack || onBackToTypeSelection}
      asDrawer={asDrawer}
      backLabel={backLabel}
      onClose={onClose}
    >
      <RadioGroup onChange={setSelected} defaultValue="-1" value={selected}>
        <Stack spacing="16px">
          {offering.allowsCheck && (
            <AccountRadio
              name="Check"
              description={checkDescription}
              type="Check"
            />
          )}
          {offering.allowsBank && (
            <AccountRadio
              name="Bank transfer"
              description={transferDescription}
              type="Bank transfer"
            />
          )}
        </Stack>
      </RadioGroup>
    </WizardContainer>
  );
};

export const WizardInvestmentDistributionPreferencesReceiveCheck = ({
  basePath,
  onBack,
  investment,
  distributionPreferenceValue,
  onFinish,
  asDrawer,
  submitText = "Continue",
  onClose,
  customAnswers,
  distributionPreference,
  showAddressDisclaimer,
}) => {
  const otherAddress =
    distributionPreference?.addressType === AddressType.Other
      ? distributionPreference?.distributionAddress
      : null;

  const addresses =
    getAddressSelection(investment.account, otherAddress).filter(
      (a) => a.value,
    ) || [];

  const selectedAddr = addresses.findIndex(
    (addr) => distributionPreference?.addressType === addr.key,
  );
  const form = useVeriForm({
    defaultValues: {
      address: selectedAddr > -1 ? addresses[selectedAddr].value : null,
    },
  });

  const { handleSubmit, reset } = form;

  const [selected, setSelected] = useState(
    selectedAddr > -1 ? selectedAddr : 0,
  );
  const [error, setError] = useState();
  const api = useApi();
  const history = useHistory();
  const { wrapApiRequest } = useStandardApiHandler();
  const onChangeAddress = (value) => {
    const index = parseInt(value);
    setSelected(index);
    reset({ address: addresses[index].value });
  };
  useEffect(() => {
    onChangeAddress(selectedAddr > -1 ? selectedAddr : 0);
  }, [selectedAddr]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSubmit = wrapApiRequest((values) => {
    return new Promise(async (resolve) => {
      const address = addresses[selected] && addresses[selected].value;
      try {
        let returnedAddress;
        if (addresses[selected].key === AddressType.Other) {
          const addressData = { ...values.address };
          returnedAddress = await createAddress(api, addressData);
        } else {
          returnedAddress = address;
        }

        if (onFinish) {
          return await onFinish(returnedAddress, addresses[selected].key);
        }

        return history.push(`${basePath}/progress`);
      } catch (e) {
        setError("There was an error saving the distribution preference");
      }
      resolve();
    });
  });

  return (
    <WizardContainer
      title="Where should we send your check?"
      onSubmit={handleSubmit(onSubmit)}
      customAnswers={customAnswers}
      rightExtra={
        <RightPanelInvestment
          investment={investment}
          distributionPreference={distributionPreferenceValue}
        />
      }
      message={error}
      onBack={onBack}
      asDrawer={asDrawer}
      submitText={submitText}
      onClose={onClose}
    >
      {addresses.length > 0 && (
        <Box data-private mb="32px">
          <RadioGroup
            onChange={onChangeAddress}
            defaultValue="0"
            value={selected.toString()}
          >
            <Stack gap="16px">
              {addresses.map((a, index) => (
                <Box key={index}>
                  <Radio
                    colorScheme="brand"
                    alignItems="flex-start"
                    size="lg"
                    value={index.toString()}
                  >
                    <Text textStyle="bodyRegular">{a.key}</Text>
                  </Radio>
                  {showAddressDisclaimer !== false && (
                    <AddressDisclaimer
                      type={a.key}
                      accountId={investment.account.id}
                      isVisible={selected === index}
                    />
                  )}
                </Box>
              ))}
            </Stack>
          </RadioGroup>
        </Box>
      )}

      <HookFormVeriAddress
        form={form}
        name={"address"}
        isDisabled={addresses[selected].key !== AddressType.Other}
        isRequired
      />
    </WizardContainer>
  );
};

export const WizardInvestmentDistributionPreferencesReceiveBank = ({
  basePath,
  onBack,
  investment,
  distributionPreferenceValue,
  onFinish,
  asDrawer,
  initialData,
  submitText = "Continue",
  onClose,
  customAnswers,
  subtitle = "Your investor returns will be deposited into this account.",
  fundingNoteHelperText = "If your bank requires additional funding instructions, please provide those here (i.e FBO John Smith #12345)",
}) => {
  const dynamicConfig = useDynamicConfig();
  const manager = dynamicConfig.get(DASHBOARD_MANAGER);
  const managerId = manager?.id || "";
  const [selected, setSelected] = useState(
    initialData?.bankAccount?.id || "-1",
  );
  const [error, setError] = useState();
  const placementsApi = usePlacementsApi();
  const history = useHistory();
  const { register, handleSubmit, formState } = useVeriForm({
    defaultValues: {},
  });
  const { errors } = formState;
  const { data } = useSWR(
    managerId
      ? `/bank-accounts?account_id=${investment.account.id}&manager_id=${managerId}`
      : `/bank-accounts?account_id=${investment.account.id}`,
    placementsApi.apiRequest,
    {
      focusThrottleInterval: 0,
    },
  );
  const bankAccounts = (data && data.data) || [];
  const getError = (fieldName) => {
    const e = getProperty(errors || {}, fieldName);
    return e && e.message;
  };

  const onChangeBankSelection = (value) => {
    setSelected(value);
  };

  const submitSelectedAccount = async () => {
    try {
      let bankAccount;
      bankAccounts.some((b) => {
        if (b.id === selected) {
          bankAccount = b;
          return true;
        }
        return false;
      });
      if (bankAccount && onFinish) return await onFinish(bankAccount);

      return history.push(`${basePath}/progress`);
    } catch (e) {
      logError(
        "🚀 ~ file: DistributionPreferencesReceive.js ~ WizardInvestmentDistributionPreferencesReceiveBank ~ submitSelectedAccount ~ e",
        e,
      );
      setError("There was an error saving the distribution preference");
    }
  };

  const onSubmit = (values) => {
    return new Promise(async (resolve) => {
      // const address = addresses[selected].value;
      try {
        const bankAccountData = {
          ...values,
          account: { id: investment.account.id },
        };

        const returnedBankAccount = await placementsApi.createResource(
          "bank-account",
          dasherize(bankAccountData),
          managerId ? { manager_id: managerId } : {},
        );

        if (onFinish) {
          return await onFinish(returnedBankAccount);
        }

        return history.push(`${basePath}/progress`);
      } catch (e) {
        setError("There was an error saving the distribution preference");
      }
      resolve();
    });
  };

  return (
    <WizardContainer
      title="Add a bank account"
      subtitle={subtitle}
      customAnswers={customAnswers}
      onSubmit={
        selected === "-1" ? handleSubmit(onSubmit) : submitSelectedAccount
      }
      rightExtra={
        <RightPanelInvestment
          investment={investment}
          distributionPreference={distributionPreferenceValue}
        />
      }
      message={error}
      onBack={onBack}
      asDrawer={asDrawer}
      submitText={submitText}
      onClose={onClose}
    >
      {bankAccounts.length > 0 && (
        <>
          <Text fontWeight="500" mb="16px">
            Select an existing bank account, or add a new one.
          </Text>
          <Box data-private mb="24px">
            <RadioGroup
              onChange={onChangeBankSelection}
              value={selected}
              defaultValue="-1"
            >
              <Stack gap="16px">
                {bankAccounts.map((b) => (
                  <Radio
                    key={b.id}
                    colorScheme="brand"
                    alignItems="flex-start"
                    size="lg"
                    value={b.id.toString()}
                  >
                    <Text textStyle="bodyRegular">{b.nickname}</Text>
                  </Radio>
                ))}
                <Radio
                  key={"false"}
                  colorScheme="brand"
                  alignItems="flex-start"
                  size="lg"
                  value={"-1"}
                >
                  <Text textStyle="bodyRegular">Add new bank account</Text>
                </Radio>
              </Stack>
            </RadioGroup>
          </Box>
        </>
      )}
      {selected === "-1" && (
        <>
          <Text fontWeight="500" mb="16px">
            Is this a checking or savings account?
          </Text>
          <RadioGroup data-private mb="36px">
            <Stack spacing={4}>
              <Radio
                {...register("accountType", {
                  required: {
                    value: true,
                  },
                })}
                colorScheme="brand"
                alignItems="flex-start"
                size="lg"
                value={"Checking"}
              >
                <Text lineHeight="24px" fontSize="16px" color="text.black">
                  Checking
                </Text>
              </Radio>
              <Radio
                {...register("accountType", {
                  required: {
                    value: true,
                  },
                })}
                colorScheme="brand"
                alignItems="flex-start"
                size="lg"
                value={"Savings"}
              >
                <Text lineHeight="24px" fontSize="16px" color="text.black">
                  Savings
                </Text>
              </Radio>
            </Stack>
          </RadioGroup>

          <Grid
            data-private
            templateColumns={{ base: "1fr", md: "1fr 1fr" }}
            gap="24px"
          >
            {/* <VeriInput display="none" {...register("id")} /> */}
            <VeriInput
              {...register("nickname", {
                required: "You must enter the account nickname",
              })}
              propsInput={{ placeholder: "Ex: My investment account" }}
              label="Account nickname"
              error={getError("nickname")}
              maxLength={accountNickNameMaxLength}
            />
            <VeriInput
              {...register("name", {
                required: "You must enter a bank name",
              })}
              label="Bank name"
              error={getError("name")}
              maxLength={bankNameMaxLength}
            />
            <VeriInput
              {...register("routingNumber", {
                required: "You must enter a routing number",
                pattern: {
                  value: /^\d{9}$/,
                  message: "You must enter a 9 digit routing number",
                },
              })}
              label="Routing number"
              error={getError("routingNumber")}
              maxLength={routingNumberMaxLength}
            />
            <VeriInput
              {...register("accountNumber", {
                required: "You must enter the account number",
              })}
              label="Account number"
              error={getError("accountNumber")}
              maxLength={accountNumberMaxLength}
            />
          </Grid>
          <Box data-private mt="28px">
            <VeriInput
              {...register("fundingNote", {})}
              label="Funding note"
              helperText={fundingNoteHelperText}
              optional
              error={getError("fundingNote")}
              maxLength={fundingNoteMaxLength}
            />
          </Box>
          <Box data-private mt="32px">
            <BankAccountVisual />
          </Box>
        </>
      )}
    </WizardContainer>
  );
};

const AccountRadio = ({ type, name, description }) => {
  return (
    <Radio
      colorScheme="brand"
      alignItems="flex-start"
      size="lg"
      value={type}
      mb="16px"
    >
      <Box ml="4px">
        <Text fontWeight="700" lineHeight="24px" fontSize="16px" mb="4px">
          {name}
        </Text>
        <Text lineHeight="24px" fontSize="16px" color="text.grey">
          {description}
        </Text>
      </Box>
    </Radio>
  );
};

export default WizardInvestmentDistributionPreferencesReceive;
